import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Toast from "../components/Toast";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminStaff } from "../store/admin";

import AddStaffModal from "./components/staff/AddStaffModal";
import EditStaffModal from "./components/staff/EditStaffModal";
import DeleteStaffModal from "./components/staff/DeleteStaffModal";
import DisableUserModal from "./components/resourcePersons/DisableUserModal";
import StaffTable from "./components/staff/StaffTable";
import StaffFilters from "./components/staff/StaffFilters";
import StaffPagination from "./components/staff/StaffPagination";

const EMPTY_ADD_FORM = {
  name: "",
  email: "",
  phone: "",
};

const EMPTY_EDIT_FORM = {
  name: "",
  email: "",
  phone: "",
  avatar: "",
};

// ===== STAFF MANAGEMENT (uses /api/admin/staff) =====
const StaffManagement = () => {
  const navigate = useNavigate();
  const {
    staff,
    staffLoading,
    staffTotal,
    staffPages,
    fetchStaff,
    createStaff,
    updateStaff,
    updateStaffStatus,
    deleteStaff,
    staffMutationLoading,
  } = useAdminStaff();

  const [isLoading, setIsLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reload, setReload] = useState(false);

  const [isSuccess, setIsSuccess] = useState(true);
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // GET /api/admin/staff?page&limit&search&status&sort
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsReloading(true);
      const result = await fetchStaff({
        page,
        limit: 10,
        search,
        status: statusFilter,
        sort,
      });
      if (cancelled) return;
      if (!result.success) {
        showToast(result.error || "Failed to fetch staff", false);
      }
      setIsLoading(false);
      setIsReloading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchStaff, page, sort, search, statusFilter, reload, showToast]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // PATCH /api/admin/staff/:id/status
  const handleToggleStatus = async () => {
    if (!selectedMember) return;
    const isActive =
      selectedMember.status ? selectedMember.status === "ACTIVE" : Boolean(selectedMember.isActive);
    const nextStatus = isActive ? "DEACTIVATED" : "ACTIVE";
    const result = await updateStaffStatus(selectedMember.id, nextStatus);
    if (!result.success) {
      showToast(result.error || "Request failed", false);
    } else {
      showToast("Success!");
      setIsDisableModalOpen(false);
      setReload(!reload);
    }
  };

  const [addFormData, setAddFormData] = useState(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState("");

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // POST /api/admin/staff
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const result = await createStaff(addFormData);
    if (!result.success) {
      setAddError(result.error || "Failed to add staff");
    } else {
      showToast("Staff Added successfully");
      setIsAddModalOpen(false);
      setAddError("");
      setAddFormData(EMPTY_ADD_FORM);
      setReload(!reload);
    }
  };

  const [editFormData, setEditFormData] = useState(EMPTY_EDIT_FORM);
  const [editError, setEditError] = useState("");

  const openEditModal = (member) => {
    setSelectedMember(member);
    setEditFormData({
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      avatar: member.avatar || member.image || "",
    });
    setEditError("");
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // PATCH /api/admin/staff/:id — body { name, email, phone, avatar }
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    const payload = {
      name: editFormData.name,
      email: editFormData.email,
      phone: editFormData.phone,
      avatar: editFormData.avatar ?? null,
    };
    const result = await updateStaff(selectedMember.id, payload);
    if (!result.success) {
      setEditError(result.error || "Failed to update staff");
    } else {
      showToast("Staff updated successfully");
      setIsEditModalOpen(false);
      setReload(!reload);
    }
  };

  // DELETE /api/admin/staff/:id
  const handleDeleteConfirm = async () => {
    if (!selectedMember) return;
    const result = await deleteStaff(selectedMember.id);
    if (!result.success) {
      showToast(result.error || "Failed to delete staff", false);
    } else {
      showToast("Staff deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedMember(null);
      setReload(!reload);
    }
  };

  const handleSearchSubmit = (value) => {
    setPage(1);
    setSearch(value);
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatusFilter(value);
  };

  const handleSortChange = (value) => {
    setPage(1);
    setSort(value);
  };

  const handleView = (member) => {
    navigate(`/admin/staff-management/${member.id}`);
  };

  const handleActivity = (member) => {
    navigate(`/admin/staff-management/${member.id}/activity`);
  };

  return (
    <div>
      <AdminNavBar
        title="Staff Management"
        context="Add and manage all staff accounts here."
      />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}
        {isLoading ? (
          <AdminLoadingState />
        ) : (
          <div className="sub-body space-y-6">
            <button
              className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-50 md:right-8"
              onClick={() => {
                const subBody = document.querySelector(".sub-body");
                if (subBody) {
                  subBody.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <FontAwesomeIcon icon={faAngleUp} />
            </button>
            <AdminSectionHeader
              title="All Staff"
              description="Manage all staff accounts from one structured workspace."
              count={`${staffTotal} staff`}
              actions={
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(true);
                  }}
                  className={adminPrimaryButton}
                >
                  Add Staff
                </button>
              }
            />

            <StaffFilters
              onSearchSubmit={handleSearchSubmit}
              statusFilter={statusFilter}
              onStatusChange={handleStatusChange}
              sort={sort}
              onSortChange={handleSortChange}
            />

            <AdminTablePanel>
              <div className="overflow-x-auto">
              <StaffTable
                staff={staff}
                isReloading={isReloading || staffLoading}
                onView={handleView}
                onActivity={handleActivity}
                onEdit={openEditModal}
                onToggleStatus={(member) => {
                  setSelectedMember(member);
                  setIsDisableModalOpen(true);
                }}
                onDelete={(member) => {
                  setSelectedMember(member);
                  setIsDeleteModalOpen(true);
                }}
              />
              </div>
            </AdminTablePanel>
            {!isReloading && staff.length !== 0 && (
              <StaffPagination page={page} lastPage={staffPages} onPageChange={setPage} />
            )}
          </div>
        )}
      </AdminPageBody>

      <AddStaffModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setAddError("");
        }}
        onSubmit={handleAddSubmit}
        isLoading={staffMutationLoading}
        error={addError}
        formData={addFormData}
        onFormChange={handleAddChange}
      />

      <EditStaffModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditError("");
        }}
        onSubmit={handleEditSubmit}
        isLoading={staffMutationLoading}
        error={editError}
        formData={editFormData}
        onFormChange={handleEditChange}
      />

      <DisableUserModal
        isOpen={isDisableModalOpen}
        user={selectedMember}
        onClose={() => setIsDisableModalOpen(false)}
        onConfirm={handleToggleStatus}
        isLoading={staffMutationLoading}
      />

      <DeleteStaffModal
        isOpen={isDeleteModalOpen}
        staffMember={selectedMember}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={staffMutationLoading}
      />
    </div>
  );
};

export default StaffManagement;